/**
 * Diacritic-insensitive text helpers for search / autocomplete filters.
 */

/**
 * Lowercase and strip combining marks (ț→t, ă→a, ș→s, etc.).
 * @param {unknown} value
 * @returns {string}
 */
export function foldDiacritics(value) {
    return String(value ?? '')
        .toLowerCase()
        // Romanian comma-below letters do not always decompose under NFD.
        .replace(/[ăâ]/g, 'a')
        .replace(/î/g, 'i')
        .replace(/[șş]/g, 's')
        .replace(/[țţ]/g, 't')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Whitespace-separated search tokens (folded). Empty query → [].
 * @param {unknown} needle
 * @returns {string[]}
 */
export function searchTokens(needle) {
    const folded = foldDiacritics(needle);
    if (!folded) {
        return [];
    }
    return folded.split(/\s+/).filter(Boolean);
}

/**
 * True when every whitespace-separated token of `needle` appears somewhere in
 * `haystack` (order-independent, diacritic-insensitive).
 * Single-token queries keep classic substring behaviour.
 * @param {unknown} haystack
 * @param {unknown} needle
 * @returns {boolean}
 */
export function includesFolded(haystack, needle) {
    const tokens = searchTokens(needle);
    if (!tokens.length) {
        return true;
    }
    const foldedHaystack = foldDiacritics(haystack);
    return tokens.every((token) => foldedHaystack.includes(token));
}

/**
 * Vuetify 3 `custom-filter` compatible matcher.
 * @param {string} value
 * @param {string} query
 * @returns {boolean}
 */
export function diacriticInsensitiveFilter(value, query) {
    return includesFolded(value, query);
}
